import { useFormContext, useWatch } from 'react-hook-form';
import { Card, CardBody, Divider } from '@vnetwork/ui/primitives';
import { formatContractAmount } from './format-contract-amount';
import { productConfigNarrative, paymentTimelineLine } from './product-config-summary';
import type { ContractFormValues } from './contract-form';

/**
 * Live order total, presented like a real quotation. Each product is a 3-tier
 * block leading into the grand Total:
 *   1. label ("Group — Product") + line amount,
 *   2. item-level narrative — the ACTUAL configured items (real package names /
 *      capacities) + billing terms,
 *   3. payment timeline — first period window + renewal cadence.
 * Read-only over form state; nothing here changes the payload.
 */
export function TotalSection() {
  const { control } = useFormContext<ContractFormValues>();
  const products = useWatch({ control, name: 'products' });
  const currency = useWatch({ control, name: 'currency_code' });
  const cur = currency || 'VND';

  const lines = (products ?? []).map((p, i) => ({
    label:
      p?.product_group_name && p?.product_name
        ? `${p.product_group_name} — ${p.product_name}`
        : `Product ${i + 1}`,
    narrative: productConfigNarrative(p?.template_name, p?.service_configoptions, { itemLevel: true }),
    timeline: paymentTimelineLine({
      first_startdate: p?.first_startdate,
      first_enddate: p?.first_enddate,
      continue_startdate: p?.continue_startdate,
      continue_enddate: p?.continue_enddate,
    }),
    amount: Number(p?.service_price_register) || 0,
  }));
  const total = lines.reduce((t, l) => t + l.amount, 0);

  return (
    <Card>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* Per-product quotation blocks — lead the eye into the Total. */}
        {lines.length > 0 && (
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {lines.map((l, i) => (
              <div key={i} style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                {/* Tier 1 — label + line amount. */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 'var(--vn-space-4)',
                    fontSize: 'var(--vn-font-size-sm)',
                    fontWeight: 600,
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {l.label}
                  </span>
                  <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatContractAmount(l.amount, cur)}
                  </span>
                </div>

                {/* Tier 2 — item-level narrative (real names) + billing. */}
                {l.narrative && (
                  <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', lineHeight: 1.5 }}>
                    {l.narrative}
                  </span>
                )}

                {/* Tier 3 — payment timeline. */}
                {l.timeline && (
                  <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)', lineHeight: 1.5 }}>
                    {l.timeline}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Divider + grand Total — the single emphasized money figure. */}
        <Divider />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--vn-space-4)',
            paddingTop: 'var(--vn-space-3)',
          }}
        >
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Total</span>
          <strong style={{ fontSize: 'var(--vn-font-size-2xl)', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
            {formatContractAmount(total, cur)}
          </strong>
        </div>
      </CardBody>
    </Card>
  );
}
